import { Component, computed, inject, linkedSignal, signal } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { LocalDatePipe, ReplacePipe } from "@kno2/common/pipes";
import { DocumentTypesService } from "@kno2/data-access/document-types";
import { IntegrationsService } from "@kno2/data-access/integrations";
import { MessagesService } from "@kno2/data-access/messages";
import { SettingsService } from "@kno2/data-access/settings";
import { MessageActionBarComponent, MessagePatientAttachmentsComponent, MessagePatientDetailsComponent } from "@kno2/features/message";
import { MessageFormManagerService, MessageFormService } from "@kno2/features/message/shared";
import { MultiAddressSnippetComponent } from "@kno2/shared/multi-address-snippet";
import { PdfViewerContainerComponent, PdfViewerOptions } from "@kno2/shared/pdf-viewer";

@Component({
    selector: "kno2-release-view",
    imports: [
        MessageActionBarComponent,
        MessagePatientDetailsComponent,
        MultiAddressSnippetComponent,
        PdfViewerContainerComponent,
        LocalDatePipe,
        ReplacePipe,
        MessagePatientAttachmentsComponent
    ],
    providers: [MessageFormManagerService],
    templateUrl: "./release-view.component.html"
})
export class ReleaseViewComponent {
    private readonly route = inject(ActivatedRoute);
    private readonly messagesService = inject(MessagesService);
    private readonly messageFormService = inject(MessageFormService);
    private readonly documentTypesService = inject(DocumentTypesService);
    private readonly integrationsService = inject(IntegrationsService);
    private readonly settingsService = inject(SettingsService);

    protected message = this.route.snapshot.data.message;
    protected messageForm = this.messageFormService.reset().form;
    protected useIntegrationDocumentTypes = this.route.snapshot.data.useIntegrationDocumentTypes;

    protected selectedAttachment = linkedSignal(() => this.message?.attachments?.[0]);
    protected notifications = computed(() => this.set360xNotifications());
    protected documentTypes = !this.useIntegrationDocumentTypes
        ? this.documentTypesService.getDocumentTypes()
        : this.integrationsService.getIntegrationDocumentTypes();

    protected deliverySummaryEnableDownload = signal(false);
    protected viewerOptions = signal<PdfViewerOptions>({
        rotate: "hidden",
        remove: "hidden",
        print: "hidden",
        download: "hidden",
        zoom: "auto"
    });

    public async ngOnInit(): Promise<void> {
        this.messageFormService.setMessageDetails(this.message).setPatientDetails(this.message.patient).setAttachments(this.message.attachments).asReadonly();
        const interoperabilitySettings = await this.settingsService.getInteroperabilitySettingsAsync();
        this.deliverySummaryEnableDownload.set(interoperabilitySettings.deliverySummaryEnableDownload);
    }

    public onAttachmentRead(event: { messageId: string; attachmentId: string }): void {
        this.messagesService.raiseAttachmentReadEvent(event.messageId, event.attachmentId).catch(console.error);
    }

    private set360xNotifications(): string[] {
        const notifications: string[] = [];
        if (this.message.conversation?.conversationId && this.message.conversation.type == "360x") {
            notifications.push("This message is part of a tracked referral", `This referral has been ${this.message.conversation.conversationStatus}`);
        }

        return notifications;
    }
}
